<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import mcpServersAPI from 'dashboard/api/captain/mcpServers';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  mcpServerId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const tools = ref([]);
const isLoading = ref(false);
const expandedTool = ref(null);
const testingTool = ref(null);
const testResults = ref({});
const toolArgs = ref({});

onMounted(async () => {
  await loadTools();
});

const loadTools = async () => {
  try {
    isLoading.value = true;
    const { data } = await mcpServersAPI.getTools(props.mcpServerId);
    tools.value = data.tools || [];
  } catch (error) {
    useAlert(t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const toggleTool = toolName => {
  if (expandedTool.value === toolName) {
    expandedTool.value = null;
  } else {
    expandedTool.value = toolName;
    // Initialize args for this tool if not already done
    if (!toolArgs.value[toolName]) {
      const tool = tools.value.find(t => t.name === toolName);
      const initialArgs = {};
      if (tool?.parameters) {
        Object.keys(tool.parameters).forEach(param => {
          initialArgs[param] = '';
        });
      }
      toolArgs.value[toolName] = initialArgs;
    }
  }
};

const updateArg = (toolName, paramName, value) => {
  if (!toolArgs.value[toolName]) {
    toolArgs.value[toolName] = {};
  }
  toolArgs.value[toolName][paramName] = value;
};

const getParamType = param => {
  if (!param) return 'text';
  if (param.type === 'number' || param.type === 'integer') return 'number';
  if (param.type === 'boolean') return 'checkbox';
  return 'text';
};

const formatArgValue = (value, type) => {
  if (type === 'number' || type === 'integer') {
    const num = Number(value);
    return isNaN(num) ? value : num;
  }
  if (type === 'boolean') {
    return value === true || value === 'true';
  }
  if (type === 'object' || type === 'array') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const testTool = async toolName => {
  try {
    testingTool.value = toolName;
    testResults.value[toolName] = null;

    const tool = tools.value.find(t => t.name === toolName);
    const args = toolArgs.value[toolName] || {};

    // Format args based on parameter types
    const formattedArgs = {};
    Object.keys(args).forEach(key => {
      const value = args[key];
      if (value !== '' && value !== null && value !== undefined) {
        const paramDef = tool?.parameters?.[key];
        formattedArgs[key] = formatArgValue(value, paramDef?.type);
      }
    });

    const { data } = await mcpServersAPI.testTool(
      props.mcpServerId,
      toolName,
      formattedArgs
    );

    testResults.value[toolName] = {
      success: data.success,
      result: data.result,
      error: data.error,
    };

    if (data.success) {
      useAlert(t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TEST_SUCCESS'));
    } else {
      useAlert(t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TEST_FAILED'));
    }
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message;
    const backtrace = error.response?.data?.backtrace;

    testResults.value[toolName] = {
      success: false,
      error: errorMessage,
      backtrace: backtrace,
    };
    useAlert(t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TEST_FAILED'));
  } finally {
    testingTool.value = null;
  }
};

const isToolExpanded = toolName => expandedTool.value === toolName;
const isToolTesting = toolName => testingTool.value === toolName;
const getTestResult = toolName => testResults.value[toolName];
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TITLE') }}
      </h3>
      <span class="text-sm text-n-slate-11">
        {{
          $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TOOLS_COUNT', {
            count: tools.length,
          })
        }}
      </span>
    </div>

    <div v-if="isLoading" class="text-center text-n-slate-11 py-8">
      {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.LOADING') }}
    </div>

    <div
      v-else-if="tools.length === 0"
      class="text-center text-n-slate-11 py-8"
    >
      {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.NO_TOOLS') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="tool in tools"
        :key="tool.name"
        class="border border-n-weak rounded-lg overflow-hidden"
      >
        <!-- Tool Header -->
        <button
          type="button"
          class="w-full flex items-center justify-between p-4 hover:bg-n-alpha-black2 transition-colors text-left"
          @click="toggleTool(tool.name)"
        >
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ tool.name }}
            </h4>
            <p class="text-xs text-n-slate-11 line-clamp-2">
              {{ tool.description }}
            </p>
          </div>
          <svg
            class="w-5 h-5 text-n-slate-11 transition-transform flex-shrink-0 ml-2"
            :class="{ 'rotate-180': isToolExpanded(tool.name) }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Tool Details -->
        <div
          v-if="isToolExpanded(tool.name)"
          class="border-t border-n-weak p-4 bg-n-alpha-black1"
        >
          <!-- Parameters -->
          <div
            v-if="Object.keys(tool.parameters || {}).length > 0"
            class="mb-4"
          >
            <h5 class="text-sm font-medium text-n-slate-12 mb-3">
              {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.PARAMETERS') }}
            </h5>
            <div class="flex flex-col gap-3">
              <div
                v-for="(param, paramName) in tool.parameters"
                :key="paramName"
                class="flex flex-col gap-1"
              >
                <label class="text-xs font-medium text-n-slate-12">
                  {{ paramName }}
                  <span
                    v-if="tool.required_parameters?.includes(paramName)"
                    class="text-n-ruby-9"
                    >*</span>
                  <span class="font-normal text-n-slate-11 ml-1">
                    ({{ param.type || 'string' }})
                  </span>
                </label>
                <p
                  v-if="param.description"
                  class="text-xs text-n-slate-11 mb-1"
                >
                  {{ param.description }}
                </p>

                <!-- For simple types -->
                <Input
                  v-if="['string', 'number', 'integer'].includes(param.type)"
                  :model-value="toolArgs[tool.name]?.[paramName] || ''"
                  :type="getParamType(param)"
                  :placeholder="param.description || `Enter ${paramName}`"
                  size="small"
                  @update:model-value="updateArg(tool.name, paramName, $event)"
                />

                <!-- For boolean -->
                <label
                  v-else-if="param.type === 'boolean'"
                  class="flex items-center gap-2"
                >
                  <input
                    type="checkbox"
                    :checked="toolArgs[tool.name]?.[paramName] || false"
                    class="rounded border-n-weak"
                    @change="
                      updateArg(tool.name, paramName, $event.target.checked)
                    "
                  />
                  <span class="text-xs text-n-slate-11">Enable</span>
                </label>

                <!-- For object/array - use textarea for JSON -->
                <textarea
                  v-else
                  :value="toolArgs[tool.name]?.[paramName] || ''"
                  :placeholder="`Enter JSON ${param.type}`"
                  class="block w-full reset-base text-xs px-3 py-2 outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 transition-all duration-500 ease-in-out resize-y min-h-[80px] font-mono outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand"
                  @input="updateArg(tool.name, paramName, $event.target.value)"
                />
              </div>
            </div>
          </div>

          <!-- No parameters message -->
          <div
            v-else
            class="text-xs text-n-slate-11 mb-4 p-3 bg-n-alpha-black2 rounded-lg"
          >
            {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.NO_PARAMETERS') }}
          </div>

          <!-- Test Button -->
          <div class="flex items-center justify-end mb-4">
            <Button
              size="sm"
              :label="$t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.TEST_BUTTON')"
              :is-loading="isToolTesting(tool.name)"
              :disabled="isToolTesting(tool.name)"
              @click="testTool(tool.name)"
            />
          </div>

          <!-- Test Results -->
          <div
            v-if="getTestResult(tool.name)"
            class="p-3 rounded-lg"
            :class="[
              getTestResult(tool.name).success
                ? 'bg-green-50 dark:bg-green-900/20'
                : 'bg-red-50 dark:bg-red-900/20',
            ]"
          >
            <h6
              class="text-xs font-medium mb-2"
              :class="
                getTestResult(tool.name).success
                  ? 'text-green-900 dark:text-green-100'
                  : 'text-red-900 dark:text-red-100'
              "
            >
              {{
                getTestResult(tool.name).success
                  ? $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.RESULT_SUCCESS')
                  : $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.RESULT_ERROR')
              }}
            </h6>
            <pre
              class="text-xs overflow-auto max-h-48 font-mono whitespace-pre-wrap"
              :class="
                getTestResult(tool.name).success
                  ? 'text-green-800 dark:text-green-200'
                  : 'text-red-800 dark:text-red-200'
              "
              >{{
                getTestResult(tool.name).success
                  ? getTestResult(tool.name).result
                  : getTestResult(tool.name).error
              }}</pre>
            <div
              v-if="
                !getTestResult(tool.name).success &&
                getTestResult(tool.name).backtrace
              "
              class="mt-2 pt-2 border-t border-red-300 dark:border-red-800"
            >
              <p
                class="text-xs font-medium text-red-900 dark:text-red-100 mb-1"
              >
                Stack Trace:
              </p>
              <pre
                class="text-xs overflow-auto max-h-32 font-mono text-red-800 dark:text-red-200 whitespace-pre-wrap"
                >{{ getTestResult(tool.name).backtrace.join('\n') }}</pre>
            </div>
          </div>

          <!-- Schema View (collapsible) -->
          <details class="mt-4">
            <summary
              class="text-xs font-medium text-n-slate-11 cursor-pointer hover:text-n-slate-12"
            >
              {{ $t('CAPTAIN.MCP_SERVERS.TOOL_TESTER.VIEW_SCHEMA') }}
            </summary>
            <pre
              class="text-xs mt-2 p-3 bg-n-alpha-black2 rounded-lg overflow-auto max-h-64 font-mono text-n-slate-11"
              >{{ JSON.stringify(tool.input_schema, null, 2) }}</pre>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>
